import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../hooks/usePermission";
import "./SearchOverlay.css";

// Sidebar list permissions by path (null / missing = always visible, e.g. Dashboard)
const PAGE_PERMISSIONS = {
  "/dashboard": null,
  "/users": "members-list",
  "/weekly-meetings": "meeting-schedules-list",
  "/weekly-fees": "meeting-fees-list",
  "/trainings": "training-list",
  "/one-to-one": "121s-list",
  "/referrals": "referrals-list",
  "/tyfcbs": "tyfcbs-list",
  "/events": "events-list",
  "/news": "news-list",
  "/feed-posts": "feed-posts-list",
  "/incomes": "incomes-list",
  "/expenses": "expenses-list",
  "/sponsors": "sponsors-list",
  "/testimonials": "testimonials-list",
  "/roles": "roles-list",
  "/settings": "settings-list",
  "/points-system": "points-system-list",
  // Client: General / Chief Guest / Power Date paused — comment only, do not remove
  // "/visitors/general": "visitors-general-list",
  // "/visitors/chief-guest": "visitors-chief-guest-list",
  // "/visitors/power-date": "visitors-power-date-list",
  "/visitors/inductions": "visitors-inductions-list",
  "/reports/tyfcbs": "reports-tyns-list",
  "/reports/biz-given": "reports-biz-given-list",
  "/reports/one-to-one": "reports-121s-list",
  "/reports/referrals": "reports-referrals-list",
  "/reports/visitors": "reports-visitors-list",
  "/reports/points": "reports-reward-points",
  "/reports/monthly-points": "reports-reward-points",
  "/reports/fees": "reports-fees-list",
  "/reports/attendance": "reports-attendance-list",
  // "/reports/training": "reports-attendance-list",
  "/reports/overall": "reports-overall-list",
  "/reports/membership-expiry-renewals": "reports-membership-expiry-renewal-list",
  "/reports/ledger": "reports-ledger-list",
  "/countries": "countries-list",
  "/states": "states-list",
  "/citys": "citys-list",
  "/regions": "regions-list",
  "/chapters": "chapters-list",
  "/admins": [
    "country-admins-list",
    "state-admins-list",
    "city-admins-list",
    "region-admins-list",
    "chapter-admins-list",
  ],
  "/user-roles": "user-roles-list",
  "/category": "business-category-list",
  "/business-sub-category": "business-sub-category-list",
  "/income-category": "income-category-list",
  "/expense-category": "expense-category-list",
  "/posts-category": "posts-category-list",
  "/sponsors-category": "sponsors-category-list",
  "/blood-groups": "blood-groups-list",
  // "/crowns": "crowns-list", // Client: Crowns paused
  // "/timers": "timers-list", // commented in Sidebar
  // "/timer-access": "timer-access-list", // commented in Sidebar
  "/chapter-finance-access": "chapter-finance-access-list",
};

// All sidebar page links grouped by category
const ALL_PAGES = [
  // Main Pages
  {
    name: "Dashboard",
    path: "/dashboard",
    icon: "bx bxs-dashboard",
    category: "Pages",
  },
  { name: "Members", path: "/users", icon: "bx bx-user", category: "Pages" },
  {
    name: "Meeting Schedules",
    path: "/weekly-meetings",
    icon: "bx bx-calendar",
    category: "Pages",
  },
  {
    name: "Meeting Fees",
    path: "/weekly-fees",
    icon: "bx bx-money",
    category: "Pages",
  },
  {
    name: "Training",
    path: "/trainings",
    icon: "bx bx-book-reader",
    category: "Pages",
  },
  {
    name: "Personal Meet",
    path: "/one-to-one",
    icon: "bx bx-conversation",
    category: "Pages",
  },
  {
    name: "Professional Connect",
    path: "/referrals",
    icon: "bx bx-share-alt",
    category: "Pages",
  },
  {
    name: "Gratitude Note",
    path: "/tyfcbs",
    icon: "bx bx-happy-heart-eyes",
    category: "Pages",
  },
  {
    name: "Events",
    path: "/events",
    icon: "bx bx-calendar-event",
    category: "Pages",
  },
  { name: "News Feed", path: "/news", icon: "bx bx-news", category: "Pages" },
  { name: "Leads", path: "/feed-posts", icon: "bx bx-file", category: "Pages" },
  {
    name: "Incomes",
    path: "/incomes",
    icon: "bx bx-dollar-circle",
    category: "Pages",
  },
  {
    name: "Expenses",
    path: "/expenses",
    icon: "bx bx-credit-card",
    category: "Pages",
  },
  {
    name: "Sponsors",
    path: "/sponsors",
    icon: "bx bx-donate-heart",
    category: "Pages",
  },
  {
    name: "Testimonials",
    path: "/testimonials",
    icon: "bx bx-message-square-dots",
    category: "Pages",
  },
  {
    name: "Roles & Permissions",
    path: "/roles",
    icon: "bx bx-lock-alt",
    category: "Pages",
  },
  {
    name: "Attendance QR Code",
    path: "/settings",
    icon: "bx bx-qr",
    category: "Pages",
  },

  // Client: Visitors submenu removed — Inductions is main menu
  {
    name: "Inductions",
    path: "/visitors/inductions",
    icon: "bx bx-user-check",
    category: "Pages",
  },
  // Client: General / Chief Guest / Power Date paused — comment only, do not remove
  // {
  //   name: "Visitors - General",
  //   path: "/visitors/general",
  //   icon: "bx bx-group",
  //   category: "Visitors",
  // },
  // {
  //   name: "Visitors - Chief Guest",
  //   path: "/visitors/chief-guest",
  //   icon: "bx bx-star",
  //   category: "Visitors",
  // },
  // {
  //   name: "Visitors - Power Date",
  //   path: "/visitors/power-date",
  //   icon: "bx bx-calendar-star",
  //   category: "Visitors",
  // },
  // {
  //   name: "Visitors - Inductions",
  //   path: "/visitors/inductions",
  //   icon: "bx bx-user-plus",
  //   category: "Visitors",
  // },

  // Reports
  {
    name: "Reports - Gratitude Note",
    path: "/reports/tyfcbs",
    icon: "bx bx-bar-chart-alt-2",
    category: "Reports",
  },
  {
    name: "Reports - BIZ Given",
    path: "/reports/biz-given",
    icon: "bx bx-bar-chart-alt-2",
    category: "Reports",
  },
  {
    name: "Reports - Personal Meet",
    path: "/reports/one-to-one",
    icon: "bx bx-bar-chart-alt-2",
    category: "Reports",
  },
  {
    name: "Reports - Professional Connect",
    path: "/reports/referrals",
    icon: "bx bx-bar-chart-alt-2",
    category: "Reports",
  },
  {
    name: "Reports - Inductions",
    path: "/reports/visitors",
    icon: "bx bx-bar-chart-alt-2",
    category: "Reports",
  },
  {
    name: "Reports - Reward Points",
    path: "/reports/points",
    icon: "bx bx-trophy",
    category: "Reports",
  },
  {
    name: "Reports - Monthly Points",
    path: "/reports/monthly-points",
    icon: "bx bx-calendar-star",
    category: "Reports",
  },
  {
    name: "Reports - Circle Pending Fee",
    path: "/reports/fees",
    icon: "bx bx-bar-chart-alt-2",
    category: "Reports",
  },
  {
    name: "Reports - Attendance",
    path: "/reports/attendance",
    icon: "bx bx-bar-chart-alt-2",
    category: "Reports",
  },
  {
    name: "Reports - Training",
    path: "/reports/training",
    icon: "bx bx-bar-chart-alt-2",
    category: "Reports",
  },
  {
    name: "Reports - Overall",
    path: "/reports/overall",
    icon: "bx bx-bar-chart-alt-2",
    category: "Reports",
  },
  {
    name: "Reports - Membership Expiry & Renewals",
    path: "/reports/membership-expiry-renewals",
    icon: "bx bx-bar-chart-alt-2",
    category: "Reports",
  },
  {
    name: "Reports - Incomes & Expenses Ledger",
    path: "/reports/ledger",
    icon: "bx bx-bar-chart-alt-2",
    category: "Reports",
  },

  // Masters
  {
    name: "Countries",
    path: "/countries",
    icon: "bx bx-globe",
    category: "Masters",
  },
  { name: "States", path: "/states", icon: "bx bx-map", category: "Masters" },
  { name: "Cities", path: "/citys", icon: "bx bx-buildings", category: "Masters" },
  {
    name: "Regions",
    path: "/regions",
    icon: "bx bx-map-pin",
    category: "Masters",
  },
  {
    name: "Circles",
    path: "/chapters",
    icon: "bx bx-book-open",
    category: "Masters",
  },
  {
    name: "Admins",
    path: "/admins",
    icon: "bx bx-user-circle",
    category: "Masters",
  },
  {
    name: "User Roles",
    path: "/user-roles",
    icon: "bx bx-id-card",
    category: "Masters",
  },
  {
    name: "Business Category",
    path: "/category",
    icon: "bx bx-category",
    category: "Masters",
  },
  {
    name: "Business Sub Category",
    path: "/business-sub-category",
    icon: "bx bx-category-alt",
    category: "Masters",
  },
  {
    name: "Income Category",
    path: "/income-category",
    icon: "bx bx-category-alt",
    category: "Masters",
  },
  {
    name: "Expense Category",
    path: "/expense-category",
    icon: "bx bx-category-alt",
    category: "Masters",
  },
  {
    name: "Leads Category",
    path: "/posts-category",
    icon: "bx bx-category-alt",
    category: "Masters",
  },
  {
    name: "Sponsors Category",
    path: "/sponsors-category",
    icon: "bx bx-category-alt",
    category: "Masters",
  },
  {
    name: "Blood Groups",
    path: "/blood-groups",
    icon: "bx bx-heart",
    category: "Masters",
  },
  // { name: "Crowns", path: "/crowns", icon: "bx bx-crown", category: "Masters" }, // Client: Crowns paused
  // { name: "Timers", path: "/timers", icon: "bx bx-timer", category: "Masters" }, // commented in Sidebar
  // {
  //   name: "Timer Access",
  //   path: "/timer-access",
  //   icon: "bx bx-time-five",
  //   category: "Masters",
  // }, // commented in Sidebar
  {
    name: "Circle Finance Access",
    path: "/chapter-finance-access",
    icon: "bx bx-wallet",
    category: "Masters",
  },
  {
    name: "Points System",
    path: "/points-system",
    icon: "bx bx-star",
    category: "Pages",
  },
];

const SearchOverlay = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  // Only active Sidebar menus that the user has permission for
  const accessiblePages = ALL_PAGES.filter((page) => {
    if (!(page.path in PAGE_PERMISSIONS)) return false;
    const permission = PAGE_PERMISSIONS[page.path];
    if (permission === null) return true;
    if (Array.isArray(permission)) {
      return permission.some((p) => hasPermission(p));
    }
    return hasPermission(permission);
  });

  // Filter pages based on search term
  const filteredPages = searchTerm.trim()
    ? accessiblePages.filter((page) =>
        page.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : accessiblePages;

  // Group filtered pages by category
  const groupedPages = filteredPages.reduce((acc, page) => {
    if (!acc[page.category]) {
      acc[page.category] = [];
    }
    acc[page.category].push(page);
    return acc;
  }, {});

  // Flat list for keyboard navigation
  const flatList = filteredPages;

  // Focus input when overlay opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setActiveIndex(0);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Scroll active item into view
  useEffect(() => {
    const activeEl = listRef.current?.querySelector(
      ".search-result-item.active",
    );
    if (activeEl) {
      activeEl.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, flatList.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flatList[activeIndex]) {
        handleNavigate(flatList[activeIndex].path);
      }
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  if (!isOpen) return null;

  let globalIndex = 0;

  return createPortal(
    <>
      <div className="search-overlay-backdrop" onClick={onClose}></div>
      <div className="search-overlay-modal">
        {/* Search Input */}
        <div className="search-overlay-header">
          <i className="bx bx-search search-overlay-icon"></i>
          <input
            ref={inputRef}
            type="text"
            className="search-overlay-input"
            placeholder="Type a command or search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
          />
          <button className="search-overlay-close" onClick={onClose}>
            <i className="bx bx-x"></i>
          </button>
        </div>

        {/* Results List */}
        <div className="search-overlay-results" ref={listRef}>
          {Object.keys(groupedPages).length === 0 ? (
            <div className="search-no-results">
              <i className="bx bx-search-alt-2"></i>
              <p>No results found for "{searchTerm}"</p>
            </div>
          ) : (
            Object.entries(groupedPages).map(([category, pages]) => (
              <div key={category} className="search-result-group">
                <div className="search-result-category">{category}</div>
                {pages.map((page) => {
                  const currentIndex = globalIndex++;
                  return (
                    <div
                      key={page.path}
                      className={`search-result-item ${currentIndex === activeIndex ? "active" : ""}`}
                      onClick={() => handleNavigate(page.path)}
                      onMouseEnter={() => setActiveIndex(currentIndex)}
                    >
                      <i className={`${page.icon} search-result-item-icon`}></i>
                      <span className="search-result-item-name">
                        {page.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default SearchOverlay;
